import "../../page/dutyRequestPage/dutyRequest.page.css";
import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { createDutyRequestMutation, updateDutyRequestMutation } from "@src/util/mutation/dutyRequest.mutation";
import { getPayLevelQuery } from "@src/util/query/paylevel.query";
import { Form, Input, Select, DatePicker, TimePicker, Modal, Checkbox, message, InputNumber, Spin } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faExternalLink } from "@fortawesome/free-solid-svg-icons";
import { CaretDownFilled, LoadingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const { Option } = Select;

/* ─── Custom Schedule Modal ─── */
const CustomScheduleModal = ({ visible, onClose, scheduleDays, setScheduleDays, frequencyDays, setFrequencyDays }) => {
    const [selectedDays, setSelectedDays] = useState([]);
    const [repeatDays, setRepeatDays] = useState("");

    const handleDayChange = (vals) => {
        setSelectedDays(vals);
        if (vals.length > 0) setRepeatDays("");
    };
    const handleInputChange = (e) => {
        setRepeatDays(e.target.value);
        if (e.target.value) setSelectedDays([]);
    };

    useEffect(() => { setScheduleDays(selectedDays); }, [selectedDays]);
    useEffect(() => { setFrequencyDays(repeatDays); }, [repeatDays]);

    return (
        <Modal open={visible} width={600} closable={false} footer={
            <div className="drawer-footer">
                <button className="drawer-filled-btn" onClick={onClose}>Save</button>
                <button className="drawer-outlined-btn" onClick={onClose}>Cancel</button>
            </div>
        }>
            <div className="modal-header-title">
                <span>Until Further Notice – Schedule</span>
                <span onClick={onClose} className="close"><FontAwesomeIcon icon={faClose} /></span>
            </div>
            <div className="modal-body">
                <b>Every</b>
                <Checkbox.Group
                    value={scheduleDays}
                    onChange={handleDayChange}
                    style={{ fontWeight: 500, display: "flex", gap: 20, flexDirection: "row", margin: "16px 0" }}
                >
                    {["mon", "tue", "wed", "thu", "fri", "sat", "sun"].map(d => (
                        <Checkbox key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</Checkbox>
                    ))}
                </Checkbox.Group>
            </div>
            <div style={{ display: "flex", alignItems: "center", padding: "0 12px" }}>
                <div style={{ borderBottom: "1px solid #D3D3D3", flex: 1 }} />
                <span style={{ margin: "0 16px", color: "#7c98b6", fontSize: 12 }}>OR</span>
                <div style={{ borderBottom: "1px solid #D3D3D3", flex: 1 }} />
            </div>
            <div className="modal-body">
                <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>Repeat after every</label>
                <Input
                    className="generic-input-control"
                    placeholder="Number of days"
                    value={frequencyDays}
                    onChange={handleInputChange}
                    type="number"
                    min={1}
                    max={364}
                />
            </div>
        </Modal>
    );
};

// parses "DD/MM/YYYY" / "DD/MM/YYYY HH:mm" strings (the wire format both the
// create mutation sends and this form pre-fills from) back into dayjs values
const parseWireDate = (val, withTime) => (val ? dayjs(val, withTime ? "DD/MM/YYYY HH:mm" : "DD/MM/YYYY") : undefined);

/* ─── New/Edit Duty Request Modal ───
   Shared by the dedicated Duty Requests page and the Report page's Schedules
   tab. `lockedSiteId` pre-selects and locks the Site field (Report page is
   already scoped to one site). `editingRequest` switches the form to edit
   mode: Site/Service Type become read-only (UpdateDutyRequestInput has no
   siteId/serviceType field - only the request's own detail fields can
   change), the mutation becomes updateDutyRequest, and only pending requests
   should ever be passed in here (rejected/pushed aren't editable). */
export const DutyRequestFormModal = ({ visible, onClose, sites, onSuccess, lockedSiteId, editingRequest }) => {
    const [form] = Form.useForm();
    const isEdit = !!editingRequest;
    const serviceType = Form.useWatch("serviceType", form) || editingRequest?.serviceType;
    const scheduleType = Form.useWatch("scheduleType", form);
    const startTime = Form.useWatch("startTime", form);

    const [scheduleDays, setScheduleDays] = useState(editingRequest?.scheduleDays || []);
    const [frequencyDays, setFrequencyDays] = useState(editingRequest?.frequencyDays || 0);
    const [customScheduleModal, setCustomScheduleModal] = useState(false);

    const { data: dutyTypeData, loading: dutyTypeLoading } = useQuery(getPayLevelQuery, {
        fetchPolicy: "network-only",
    });
    const dutyTypes = dutyTypeData?.getPayLevel?.response || [];
    // Duty types are tagged guarding/mobile via PayLevel.category - a guarding
    // request should only ever offer guarding duty types and vice versa. Legacy
    // duty types with no category recorded at all are shown either way rather
    // than made unselectable just because they predate this field.
    const filteredDutyTypes = useMemo(
        () => dutyTypes.filter((dt) => !dt.category || dt.category === serviceType),
        [dutyTypes, serviceType]
    );

    // If Service Type changes to something the currently-picked duty type no
    // longer belongs to, drop that selection rather than silently submitting a
    // mismatched pair.
    useEffect(() => {
        if (!dutyTypes.length) return;
        const current = form.getFieldValue('dutyTypeId');
        if (current && !filteredDutyTypes.some((dt) => dt._id === current)) {
            form.setFieldValue('dutyTypeId', undefined);
        }
    }, [serviceType]);

    const [createRequest, { loading: creating }] = useMutation(createDutyRequestMutation);
    const [updateRequest, { loading: updating }] = useMutation(updateDutyRequestMutation);
    const loading = creating || updating;

    useEffect(() => {
        if (!visible) return;
        if (editingRequest) {
            form.setFieldsValue({
                siteId: editingRequest.siteId || lockedSiteId,
                serviceType: editingRequest.serviceType,
                dutyTypeId: editingRequest.dutyTypeId,
                priority: editingRequest.priority,
                scheduleType: editingRequest.scheduleType,
                preferredDate: parseWireDate(editingRequest.preferredDate, false),
                // For a guarding request preferredStartTime/preferredEndTime carry the
                // right clock time on an incidental date (see handleSave below) - safe
                // to prefill either way since the guarding branch's field is a
                // TimePicker, which only ever reads the time-of-day portion.
                startTime: parseWireDate(editingRequest.preferredStartTime, true),
                endTime: parseWireDate(editingRequest.preferredEndTime, true),
                numberOfDuties: editingRequest.numberOfDuties,
                notes: editingRequest.notes,
            });
            setScheduleDays(editingRequest.scheduleDays || []);
            setFrequencyDays(editingRequest.frequencyDays || 0);
        } else if (lockedSiteId) {
            form.setFieldValue("siteId", lockedSiteId);
        }
    }, [visible, editingRequest, lockedSiteId]);

    useEffect(() => {
        if (startTime && dayjs(startTime).isValid()) {
            form.setFieldValue("endTime", dayjs(startTime).add(1, "hour"));
        }
    }, [startTime]);

    useEffect(() => {
        if (scheduleType === "ufn") setCustomScheduleModal(true);
    }, [scheduleType]);

    const handleClose = () => {
        form.resetFields();
        setScheduleDays([]);
        setFrequencyDays(0);
        onClose();
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            const sharedFields = {
                dutyTypeId: values.dutyTypeId,
                preferredDate: values.preferredDate
                    ? dayjs(values.preferredDate).format("DD/MM/YYYY") : undefined,
                preferredStartTime: values.startTime
                    ? dayjs(values.startTime).format("DD/MM/YYYY HH:mm") : undefined,
                preferredEndTime: values.endTime
                    ? dayjs(values.endTime).format("DD/MM/YYYY HH:mm") : undefined,
                priority: values.priority,
                notes: values.notes,
                numberOfDuties: values.numberOfDuties,
                scheduleType: values.scheduleType,
                scheduleDays: scheduleDays.length > 0 ? scheduleDays : undefined,
                frequencyDays: frequencyDays > 0 ? Number(frequencyDays) : undefined,
            };

            if (isEdit) {
                await updateRequest({ variables: { input: { requestId: editingRequest._id, ...sharedFields } } });
                message.success("Duty request updated successfully");
            } else {
                await createRequest({
                    variables: { input: { siteId: values.siteId, serviceType: values.serviceType, ...sharedFields } },
                });
                message.success("Duty request submitted successfully");
            }
            handleClose();
            onSuccess();
        } catch (err) {
            if (err?.message) message.error(err.message);
        }
    };

    return (
        <>
            <CustomScheduleModal
                visible={customScheduleModal}
                onClose={() => setCustomScheduleModal(false)}
                scheduleDays={scheduleDays}
                setScheduleDays={setScheduleDays}
                frequencyDays={frequencyDays}
                setFrequencyDays={setFrequencyDays}
            />

            <Modal open={visible} width={760} style={{ top: 20 }} closable={false} footer={
                <div className="drawer-footer">
                    <button
                        className={loading ? "drawer-filled-btn disabled-btn" : "drawer-filled-btn"}
                        onClick={handleSave}
                    >
                        {loading && <Spin indicator={<LoadingOutlined style={{ color: "#fff", marginRight: 8 }} spin />} />}
                        {isEdit ? "Save Changes" : "Submit Request"}
                    </button>
                    <button className="drawer-outlined-btn" onClick={handleClose}>Cancel</button>
                </div>
            }>
                <div className="modal-header-title">
                    <span>{isEdit ? "Edit Duty Request" : "New Duty Request"}</span>
                    <span className="close" onClick={handleClose}><FontAwesomeIcon icon={faClose} /></span>
                </div>

                <div className="modal-body">
                    <Form form={form} layout="vertical" requiredMark={false}>

                        {/* Site - locked to the current site when opened from the Report
                            page, read-only entirely once editing (site can't change) */}
                        <Form.Item
                            name="siteId"
                            label="Site"
                            rules={[{ required: true, message: "Please select a site" }]}
                        >
                            <Select
                                className="custom-select"
                                placeholder="Select site..."
                                suffixIcon={<CaretDownFilled />}
                                showSearch
                                optionFilterProp="children"
                                disabled={isEdit || !!lockedSiteId}
                            >
                                {sites.map(s => (
                                    <Option key={s._id} value={s._id}>
                                        <div>
                                            <span style={{ fontWeight: 500 }}>{s.sitename}</span>
                                            {s.postcode && <span style={{ fontSize: 12, color: "#7c98b6", marginLeft: 8 }}>{s.postcode}</span>}
                                        </div>
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        {/* Service Type + Duty Type row */}
                        <div style={{ display: "flex", gap: 16 }}>
                            <Form.Item
                                name="serviceType"
                                label="Service Type"
                                style={{ flex: 1 }}
                                rules={[{ required: true, message: "Please select a service type" }]}
                            >
                                <Select
                                    className="custom-select"
                                    placeholder="Select service type..."
                                    suffixIcon={<CaretDownFilled />}
                                    disabled={isEdit}
                                >
                                    <Option value="guarding">Guarding</Option>
                                    <Option value="mobile">Mobile Response</Option>
                                </Select>
                            </Form.Item>

                            <Form.Item
                                name="dutyTypeId"
                                label="Duty Type"
                                style={{ flex: 1 }}
                                rules={[{ required: true, message: "Please select a duty type" }]}
                            >
                                <Select
                                    className="custom-select"
                                    placeholder={serviceType ? "Select duty type..." : "Select a service type first"}
                                    suffixIcon={<CaretDownFilled />}
                                    loading={dutyTypeLoading}
                                    disabled={!serviceType}
                                    showSearch
                                    optionFilterProp="children"
                                >
                                    {filteredDutyTypes.map(dt => (
                                        <Option key={dt._id} value={dt._id}>{dt.name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </div>

                        {/* Mobile fields */}
                        {serviceType === "mobile" && (
                            <>
                                <div style={{ display: "flex", gap: 16 }}>
                                    <Form.Item
                                        style={{ flex: 1 }}
                                        name="priority"
                                        label="Priority"
                                        rules={[{ required: true, message: "Please select priority" }]}
                                    >
                                        <Select className="custom-select" placeholder="Select priority..." suffixIcon={<CaretDownFilled />}>
                                            {[
                                                { value: "high", color: "#FF4D4F", label: "High" },
                                                { value: "medium", color: "#FA8C16", label: "Medium" },
                                                { value: "low", color: "#52C41A", label: "Low" },
                                            ].map(p => (
                                                <Option key={p.value} value={p.value}>
                                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                        <span style={{ width: 10, height: 10, borderRadius: "50%", background: p.color, display: "inline-block" }} />
                                                        {p.label}
                                                    </div>
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>

                                    <Form.Item
                                        style={{ flex: 1 }}
                                        name="scheduleType"
                                        label={
                                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                <span>Schedule Frequency</span>
                                                {scheduleType === "ufn" && (
                                                    <span className="link" style={{ fontSize: 12 }} onClick={() => setCustomScheduleModal(true)}>
                                                        {scheduleDays.length > 0
                                                            ? `(${scheduleDays.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(", ")})`
                                                            : `(Every ${frequencyDays} days)`
                                                        }
                                                    </span>
                                                )}
                                            </div>
                                        }
                                        rules={[{ required: true, message: "Please select schedule frequency" }]}
                                    >
                                        <Select className="custom-select" placeholder="Select frequency..." suffixIcon={<CaretDownFilled />}>
                                            <Option value="one-off">One-Off</Option>
                                            <Option value="ufn">
                                                <span style={{ display: "flex", justifyContent: "space-between" }}>
                                                    Until Further Notice
                                                    <FontAwesomeIcon icon={faExternalLink} />
                                                </span>
                                            </Option>
                                        </Select>
                                    </Form.Item>
                                </div>

                                <div style={{ display: "flex", gap: 16 }}>
                                    <Form.Item
                                        style={{ flex: 1 }}
                                        name="startTime"
                                        label="Preferred Start"
                                        rules={[{ required: true, message: "Please enter start time" }]}
                                    >
                                        <DatePicker showTime className="generic-input-control" placeholder="Start date & time" format="DD/MM/YYYY HH:mm" style={{ width: "100%" }} />
                                    </Form.Item>
                                    <Form.Item
                                        style={{ flex: 1 }}
                                        name="endTime"
                                        label="Preferred End"
                                        rules={[{ required: true, message: "Please enter end time" }]}
                                    >
                                        <DatePicker showTime className="generic-input-control" placeholder="End date & time" format="DD/MM/YYYY HH:mm" style={{ width: "100%" }} />
                                    </Form.Item>
                                </div>
                            </>
                        )}

                        {/* Guarding fields */}
                        {serviceType === "guarding" && (
                            <>
                                <div style={{ display: "flex", gap: 16 }}>
                                    <Form.Item style={{ flex: 1 }} name="preferredDate" label="Preferred Date">
                                        <DatePicker className="generic-input-control" placeholder="DD/MM/YYYY" format="DD/MM/YYYY" style={{ width: "100%" }} />
                                    </Form.Item>
                                    <Form.Item style={{ flex: 1 }} name="startTime" label="Start Time">
                                        <TimePicker className="generic-input-control" format="HH:mm" minuteStep={15} style={{ width: "100%" }} />
                                    </Form.Item>
                                    <Form.Item style={{ flex: 1 }} name="endTime" label="End Time">
                                        <TimePicker className="generic-input-control" format="HH:mm" minuteStep={15} style={{ width: "100%" }} />
                                    </Form.Item>
                                </div>
                                <Form.Item
                                    name="numberOfDuties"
                                    label="Number of Duties"
                                    rules={[{ required: true, message: "Please enter number of duties" }]}
                                    style={{ maxWidth: 200 }}
                                >
                                    <InputNumber
                                        className="generic-input-control"
                                        min={1}
                                        max={100}
                                        placeholder="1"
                                        style={{ width: "100%" }}
                                    />
                                </Form.Item>
                            </>
                        )}

                        {/* Notes */}
                        {serviceType && (
                            <Form.Item name="notes" label="Additional Notes">
                                <Input.TextArea
                                    className="generic-input-control"
                                    placeholder="Any specific requirements or details..."
                                    rows={3}
                                    style={{ height: "auto", resize: "none" }}
                                />
                            </Form.Item>
                        )}

                    </Form>
                </div>
            </Modal>
        </>
    );
};
